import { API_ORIGIN, apiUrl } from "./apiOrigin";
// estate_data.js — loading and assembling the estate model's inputs, shared
// by the estate overview (warehouse_view.js) and the line-by-line Detailed
// screen (stock_excess.js), so the two can never disagree about which months
// count, which branches are current, or what is spare (2026-10).
//
// loadEstateInputs   fetches everything and works out each branch's usage
//                    window and freshness (dispensed / generic basis).
// prescribedModelOf  the prescribed-item basis built from those inputs.
// estateRowsFor      rows + pending map for either basis, ready for
//                    estate_model.matchEstate.
//
// The arithmetic itself stays in estate_model.js (balance, worst month,
// matching) and estate_prescribed.js (who can fill which prescription).

import { buildRows, buildPendingMap, normaliseDrug, chooseUsageWindow, assessBranchesByWindow, packetsOf } from "./estate_model";
import { buildPrescribedInputs, prescribedCoverage } from "./estate_prescribed";
const API = apiUrl("");
const GRACE_DAYS = 5;

// The prescribed screens do not consume the legacy dispensed summary or
// its coverage calculation. Keep those requests off their loading path.
async function loadPrescribedInputs({
  groupId,
  monthsAverage,
  includeCompletedTransfers,
  onProgress
}) {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`
  };
  const read = async (url, options = {}) => {
    const stage = url.split("?")[0],
      startedAt = Date.now();
    onProgress?.({
      stage,
      status: "pending",
      startedAt
    });
    try {
      const response = await fetch(`${API}/${url}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });
      if (!response.ok) throw new Error(`Could not load ${stage} (HTTP ${response.status})`);
      const data = await response.json();
      onProgress?.({
        stage,
        status: "done",
        startedAt,
        elapsedMs: Date.now() - startedAt
      });
      return data;
    } catch (error) {
      onProgress?.({
        stage,
        status: "failed",
        startedAt,
        elapsedMs: Date.now() - startedAt
      });
      throw error;
    }
  };
  onProgress?.({
    stage: "stockDmdIdentity",
    status: "waiting"
  });
  const inventoryRequest = read(`ggetInventory?groupId=${encodeURIComponent(groupId)}`).then(data => {
    if (!Array.isArray(data)) throw new Error("Inventory response is not a list");
    return data;
  });
  // Identity depends on inventory only, not on transfers or usage completing.
  const identityRequest = inventoryRequest.then(async inventory => {
    const gtins = [...new Set(inventory.filter(item => String(item.status || "").toLowerCase().includes("shelf") && packetsOf(item) > 0).map(item => item.gtin).filter(Boolean))];
    if (!gtins.length) {
      onProgress?.({
        stage: "stockDmdIdentity",
        status: "done",
        elapsedMs: 0
      });
      return {};
    }
    const result = await read("stockDmdIdentity", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        gtins
      })
    });
    return result.identity || {};
  });
  const [inventoryData, transferData, prescribed, identity] = await Promise.all([inventoryRequest, read(`getTransfers?groupId=${encodeURIComponent(groupId)}${includeCompletedTransfers ? "&includeCompleted=1" : ""}`), read("usagePrescribedDemand", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      groupId,
      monthsBack: monthsAverage + 1,
      compact: true
    })
  }), identityRequest]);
  const demand = Array.isArray(prescribed.matchTable) ? {
    ...prescribed,
    items: (prescribed.items || []).map(item => ({
      ...item,
      match: item.matchIndex == null ? null : prescribed.matchTable[item.matchIndex] || null
    }))
  } : prescribed;
  return {
    members: JSON.parse(localStorage.getItem("members") || "[]"),
    inventoryData,
    transferData: Array.isArray(transferData) ? transferData : [],
    prescribed: demand,
    identity,
    usage: {
      lookup: {},
      meta: {},
      monthly: {}
    },
    usageWindows: {},
    branchFreshness: new Map()
  };
}
export async function loadEstateInputs({
  groupId,
  monthsAverage = 3,
  includeCompletedTransfers = false,
  prescribedOnly = false,
  onProgress
}) {
  if (prescribedOnly) return loadPrescribedInputs({
    groupId,
    monthsAverage,
    includeCompletedTransfers,
    onProgress
  });
  const h = {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`
  };
  // One month more than we need, so a branch whose newest month is not yet
  // full has somewhere to fall back to without a second round trip.
  const usageMonthsBack = monthsAverage + 1;
  const [iRes, uRes, tRes, cRes] = await Promise.all([fetch(`${API}/ggetInventory?groupId=${groupId}`, {
    headers: h
  }), fetch(`${API}/getUsage3MonthsGroup?groupId=${groupId}&monthsBack=${usageMonthsBack}`, {
    headers: h
  }), fetch(`${API}/getTransfers?groupId=${groupId}${includeCompletedTransfers ? "&includeCompleted=1" : ""}`, {
    headers: h
  }),
  // Same endpoint the "Usage uploads" coverage table uses to flag a short or
  // partial month. If it is unavailable the screens still work (graceOk).
  fetch(`${API}/usageUploadCoverage`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...h
    },
    body: JSON.stringify({
      groupId,
      months: 6
    })
  }).catch(() => null)]);
  const iData = await iRes.json();
  const uData = await uRes.json();
  const tData = await tRes.json();
  let cData = null;
  if (cRes && cRes.ok) {
    try {
      cData = await cRes.json();
    } catch {
      cData = null;
    }
  }
  const inventoryData = Array.isArray(iData) ? iData : [];

  // Prescribed basis inputs. Neither may break the screen: if they fail, the
  // generic basis still works.
  let prescribed = null,
    identity = {};
  try {
    const [pRes, idRes] = await Promise.all([fetch(`${API}/usagePrescribedDemand`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...h
      },
      body: JSON.stringify({
        groupId,
        monthsBack: monthsAverage + 1
      })
    }), fetch(`${API}/stockDmdIdentity`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...h
      },
      body: JSON.stringify({
        gtins: [...new Set(inventoryData.map(i => i.gtin).filter(Boolean))]
      })
    })]);
    prescribed = pRes.ok ? await pRes.json() : {
      available: false,
      error: `HTTP ${pRes.status}`
    };
    identity = idRes.ok ? (await idRes.json()).identity || {} : {};
  } catch (e) {
    prescribed = {
      available: false,
      error: e.message
    };
  }

  // The group's member list is already in local storage; nothing here invents
  // a second way to get it.
  const members = JSON.parse(localStorage.getItem("members") || "[]");

  // Which months are actually full, per branch. If the coverage call failed,
  // the newest complete month is trusted only a few days into the next one.
  const graceOk = new Date().getDate() > GRACE_DAYS;
  const typicalByMonth = {};
  const coverageByPharmacy = {};
  if (cData) {
    (cData.months || []).forEach(m => {
      typicalByMonth[m.month] = m.typicalTradingDays;
    });
    (cData.pharmacies || []).forEach(p => {
      const byMonth = {};
      (p.months || []).forEach(c => {
        byMonth[c.month] = c;
      });
      coverageByPharmacy[p.pharmacyId] = byMonth;
    });
  }
  // The months the usage endpoint returned, oldest first, read off the data.
  const monthsAsc = Array.isArray(uData) && uData[0]?.monthly ? uData[0].monthly.map(m => m.month) : [];
  const hasAnyUsage = new Set();
  (Array.isArray(uData) ? uData : []).forEach(item => {
    if ((item.monthly || []).some(m => (m.qty || 0) > 0)) hasAnyUsage.add(item.pharmacyId);
  });
  const usageWindows = {};
  const pids = new Set([...members.map(m => m.pharmacyId), ...hasAnyUsage]);
  for (const pid of pids) {
    usageWindows[pid] = chooseUsageWindow({
      monthsAsc,
      coverageByMonth: coverageByPharmacy[pid] || {},
      typicalByMonth,
      n: monthsAverage,
      graceOk
    });
  }
  const branchFreshness = assessBranchesByWindow({
    monthsAsc,
    coverageByPharmacy,
    typicalByMonth,
    hasAnyUsage,
    members,
    n: monthsAverage,
    graceOk
  });

  // Usage lookup / monthly, trimmed to each branch's OWN window and divided
  // by the window (a full month with no dispensing is a real zero).
  const lookup = {},
    meta = {},
    monthly = {};
  (Array.isArray(uData) ? uData : []).forEach(item => {
    const parsed = normaliseDrug(item.drugName);
    const key = `${item.pharmacyId}_${parsed.key}`;
    const win = usageWindows[item.pharmacyId]?.months || monthsAsc.slice(-monthsAverage);
    const winSet = new Set(win);
    const trimmed = (item.monthly || []).filter(m => winSet.has(m.month));
    const sum = trimmed.reduce((t, m) => t + (m.qty || 0), 0);
    const avg = win.length ? sum / win.length : 0;
    lookup[key] = (lookup[key] || 0) + avg;
    if (!meta[key]) {
      meta[key] = {
        pharmacyId: item.pharmacyId,
        drugName: item.drugName,
        snomed: item.snomed,
        cleanName: parsed.key,
        packSize: parseFloat(item.packSize) || 0
      };
    }
    if (!monthly[key]) monthly[key] = trimmed.map(m => ({
      month: m.month,
      qty: m.qty || 0
    }));else trimmed.forEach((m, i) => {
      if (monthly[key][i]) monthly[key][i].qty += m.qty || 0;
    });
  });
  return {
    members,
    inventoryData,
    transferData: Array.isArray(tData) ? tData : [],
    usage: {
      lookup,
      meta,
      monthly
    },
    usageWindows,
    branchFreshness,
    prescribed,
    identity
  };
}

// The prescribed-item basis: demand, windows and freshness from the prescribed
// side of the paired usage report. null when there is no prescribed data.
export function prescribedModelOf({
  requirePrescribed = false,
  prescribed,
  members = [],
  inventoryData = [],
  identity = {},
  monthsAhead = 3,
  monthsAverage = 3
}) {
  if (!prescribed?.available && !requirePrescribed) return null;
  prescribed = prescribed?.available ? prescribed : {
    available: false,
    items: [],
    coverage: [],
    monthsAsc: []
  };
  const cov = prescribedCoverage(prescribed);
  const windows = {};
  const pids = new Set([...members.map(m => m.pharmacyId), ...cov.hasAnyUsage]);
  for (const pid of pids) {
    windows[pid] = chooseUsageWindow({
      monthsAsc: cov.monthsAsc,
      coverageByMonth: cov.coverageByPharmacy[pid] || {},
      typicalByMonth: {},
      n: monthsAverage,
      graceOk: false
    });
  }
  const fresh = assessBranchesByWindow({
    monthsAsc: cov.monthsAsc,
    coverageByPharmacy: cov.coverageByPharmacy,
    typicalByMonth: {},
    hasAnyUsage: cov.hasAnyUsage,
    members,
    n: monthsAverage,
    graceOk: false
  });
  // Branches with no prescribed report say so in their own words.
  for (const [pid, f] of fresh) if (f.status === "red" && !cov.hasAnyUsage.has(pid)) fresh.set(pid, {
    ...f,
    why: "no prescribed usage uploaded"
  });
  const built = buildPrescribedInputs({
    inventoryData,
    identity,
    demand: prescribed,
    windows,
    monthsAhead,
    n: monthsAverage
  });
  return {
    windows,
    fresh,
    built
  };
}

// Rows for either basis. prescribedModel null = generic (dispensed) basis.
export function estateRowsFor({
  prescribedModel,
  inventoryData,
  usage,
  members,
  monthsAhead,
  transferData,
  branchFreshness
}) {
  const pending = buildPendingMap(transferData || [], prescribedModel ? prescribedModel.built.keyOfDrug : undefined);
  const freshness = prescribedModel ? prescribedModel.fresh : branchFreshness;
  const rows = prescribedModel ? buildRows({
    inventoryData: prescribedModel.built.inventoryData,
    usageLookup: prescribedModel.built.usage.lookup,
    usageMeta: prescribedModel.built.usage.meta,
    usageMonthly: prescribedModel.built.usage.monthly,
    members,
    monthsAhead,
    pending,
    freshness,
    zeroUsageIsKnown: true
  }) : buildRows({
    inventoryData,
    usageLookup: usage.lookup,
    usageMeta: usage.meta,
    usageMonthly: usage.monthly,
    members,
    monthsAhead,
    pending,
    freshness
  });
  return {
    rows,
    pending,
    freshness
  };
}
